import ApiUrl from 'enum/api-url.enum';
import httpService from 'services/http/http.service';

/**
 * Object-storage URL Processing Utility (AWS S3 / Alibaba OSS / CareInn CDN)
 *
 * Resolves URLs into a renderable variant: object-storage hosts are pre-signed (or
 * served from a TTL'd in-memory cache); other URLs are returned as-is so
 * callers can append the apikey for the backend CDN themselves.
 *
 * Core logic:
 *   1. If a cached pre-signed URL exists and is still within its TTL window
 *      (minus the 5-minute buffer) → return it.
 *   2. Else if the URL needs object-storage presign (amazonaws.com /
 *      aliyuncs.com / cdn.careinn.com, and not already signed) → request a
 *      fresh pre-signed URL from the backend, cache it, return it.
 *   3. Else → return the input URL unchanged.
 *
 * Pass `{ forceRefresh: true }` to bypass the TTL cache and always request a
 * fresh pre-signed URL — used by `<CachedImg>`'s on-error retry path and by
 * `imageResolver`'s demo-mode fetch retry.
 */

// In-memory cache for pre-signed URLs with expiry
const urlCache = new Map();

/**
 * True for AWS S3, Alibaba OSS, CareInn CDN, or already-signed object URLs.
 * These must not receive `?apikey=`.
 */
export function isObjectStorageUrl(url) {
  if (typeof url !== 'string') return false;
  const u = url.toLowerCase();
  return (
    u.includes('amazonaws.com') ||
    u.includes('aliyuncs.com') ||
    u.includes('cdn.careinn.com') ||
    u.includes('signature=') ||
    u.includes('x-amz-signature=')
  );
}

/**
 * True when the URL points at private object storage but has no Signature yet.
 */
export function needsObjectStoragePresign(url) {
  if (typeof url !== 'string') return false;
  const u = url.toLowerCase();
  const objectHost =
    u.includes('cdn.careinn.com') || u.includes('aliyuncs.com') || u.includes('amazonaws.com');
  if (!objectHost) return false;
  return !(u.includes('signature=') || u.includes('x-amz-signature='));
}

class AWSUrlProcessor {
  constructor() {
    this.cacheExpiryBuffer = 300000; // 5 minutes buffer before actual expiry
  }

  /**
   * @returns {string|null} cached pre-signed URL if still within TTL, else null
   */
  getCachedUrl(originalUrl) {
    const cached = urlCache.get(originalUrl);
    if (!cached) return null;

    const timeUntilExpiry = cached.expiryTime - Date.now();
    if (timeUntilExpiry > this.cacheExpiryBuffer) {
      return cached.url;
    }

    urlCache.delete(originalUrl);
    return null;
  }

  cacheUrl(originalUrl, preSignedUrl, expirySeconds = 3600) {
    urlCache.set(originalUrl, {
      url: preSignedUrl,
      expiryTime: Date.now() + expirySeconds * 1000,
      cached: Date.now(),
    });
  }

  /**
   * Resolve a URL to a renderable variant.
   *
   * @param {string} inputUrl
   * @param {{ forceRefresh?: boolean }} [options]
   * @returns {Promise<string>}
   */
  async processUrl(inputUrl, { forceRefresh = false } = {}) {
    if (!inputUrl) return inputUrl;

    if (forceRefresh) {
      urlCache.delete(inputUrl);
    } else {
      const cachedUrl = this.getCachedUrl(inputUrl);
      if (cachedUrl) return cachedUrl;
    }

    if (!needsObjectStoragePresign(inputUrl)) {
      return inputUrl;
    }

    try {
      const preSignedUrl = await this.getPreSignedUrl(inputUrl);
      if (preSignedUrl) {
        this.cacheUrl(inputUrl, preSignedUrl);
        return preSignedUrl;
      }
    } catch (error) {
      console.error('AWSUrlProcessor pre-sign failed:', error);
    }

    return inputUrl;
  }

  /**
   * @deprecated Use needsObjectStoragePresign — kept for callers that still check AWS only.
   */
  isAwsUrl(url) {
    return needsObjectStoragePresign(url) || isObjectStorageUrl(url);
  }

  /**
   * Request a pre-signed URL from the backend.
   * @returns {Promise<string|null>}
   */
  async getPreSignedUrl(originalUrl) {
    try {
      const queryParam = `file=${encodeURIComponent(originalUrl)}`;
      const apiPath = `${ApiUrl.S3PRESIGNEDURL}?${queryParam}`;
      const response = await httpService.get(apiPath);

      if (response && response.data && response.data.presigned_url) {
        return response.data.presigned_url;
      }
      console.error('Pre-signing API response missing presigned_url:', response);
      return null;
    } catch (error) {
      console.error('Pre-signing request failed:', error);
      return null;
    }
  }

  /**
   * Drop expired entries (maintenance hook; safe to call but not required —
   * processUrl already evicts on read).
   */
  clearExpiredCache() {
    const now = Date.now();
    for (const [key, value] of urlCache.entries()) {
      if (value.expiryTime <= now) {
        urlCache.delete(key);
      }
    }
  }

  getCacheStats() {
    return {
      size: urlCache.size,
      entries: Array.from(urlCache.entries()).map(([key, value]) => ({
        originalUrl: key,
        expiryTime: new Date(value.expiryTime).toISOString(),
        cached: new Date(value.cached).toISOString(),
        isExpired: value.expiryTime <= Date.now(),
      })),
    };
  }
}

function createAWSUrlProcessor() {
  return new AWSUrlProcessor();
}

async function processUrl(url, options) {
  const processor = createAWSUrlProcessor();
  return await processor.processUrl(url, options);
}

async function processUrls(urls, options) {
  const processor = createAWSUrlProcessor();
  return await Promise.all(urls.map((url) => processor.processUrl(url, options)));
}

function getCacheStats() {
  const processor = createAWSUrlProcessor();
  return processor.getCacheStats();
}

const obj = {
  AWSUrlProcessor,
  createAWSUrlProcessor,
  processUrl,
  processUrls,
  getCacheStats,
  isObjectStorageUrl,
  needsObjectStoragePresign,
};

export default obj;
